import { useState, FormEvent } from "react";
import {
  Typography,
  Divider,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Stack,
  Alert,
  CircularProgress,
  Box,
} from "@mui/material";
import {
  AuthBox,
  LeftContent,
  RightContent,
  StyledTextField,
  FlexRowBox,
  FixedHeightTextBox,
  LoginRow
} from "./Authentication.styles";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import BonisoftLogo from "../../common/ui/BonisoftLogo";
import { useTranslation } from "react-i18next";
import { SimpleLink } from "../../components/SimpleLink";
import { doApiPost } from "../../api/Requests";
import { authenticationStateHandler } from "../logic/AuthenticationStateHandler";
import { LoginSuccessfulResponse } from "../model/LoginSuccessfulResponse";
import { keyValueStorage } from "../../common/logic/KeyValueStore";
import { useLocale } from "../../hooks/UseLocale";
import AuthLayout from "./AuthLayout";
import LanguageSwitcher from "../../common/ui/LanguageSwitcher";
import { useAppDispatch } from "../../hooks/StoreHooks";
import { setFeatureFlagList } from "../../store/reducers/loginMetadataReducer";
import { useSearchParams } from "react-router-dom";
import { Permission } from "../../types/permissionType";
import { GeneralButton } from "../../common/ui/GeneralButton";

interface Props {
  navigateToUrlWhenLoggedIn: string;
}

export function loginAndReturnTo(url: string) {
  window.location.href = `/login?returnUrl=${encodeURIComponent(url)}`;
}

export function LoginScreen({ navigateToUrlWhenLoggedIn }: Props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const locale = useLocale();

  const returnUrl = searchParams.get("returnUrl");
  let urlWhenLoggedIn = decodeURIComponent(
    returnUrl ?? navigateToUrlWhenLoggedIn
  );

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const login = async (email: string, password: string): Promise<LoginSuccessfulResponse | null> => {
    const result = await doApiPost<any, LoginSuccessfulResponse>(
      "/api/login",
      { email, password },
      {
        handleUnauthenticated: false,
        credentials: "include"
      }
    );
    return result.success() ? result.result! : null;
  };

  const saveLoginMetadata = (
    role: string,
    features: string[],
    supportedLanguages: string[],
    assignedPermissions: Permission[]
  ) => {
    keyValueStorage.setObject("userRole", role);
    keyValueStorage.setObject("featureFlags", features);
    keyValueStorage.setObject("supportedLanguages", supportedLanguages);
    keyValueStorage.setObject("assignedPermissions", assignedPermissions);
  };

  const getPostLoginUrl = (features: string[], locale: string, fallbackUrl: string) => {
    return features.includes("TestCompany") ? `/${locale}/actionmodules` : fallbackUrl;
  };

  const handleSignIn = async (event: FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setIsError(false);
    const loginResult = await login(email, password);
    if (!loginResult) {
      setIsLoading(false);
      setIsError(true);
      return;
    }
    const { accessToken, csrfToken, role, features, supportedLanguages, assignedPermissions } = loginResult;
    saveLoginMetadata(role, features, supportedLanguages, assignedPermissions);
    const isAuthenticated = await authenticationStateHandler.refreshProfileAndSetAuthenticated(accessToken, csrfToken);
    if (isAuthenticated) {
      const destinationUrl = getPostLoginUrl(features, locale, urlWhenLoggedIn);
      window.location.href = destinationUrl;
    } else {
      setIsLoading(false);
      setIsError(true);
    }
  };

  return (
    <AuthLayout>
      <AuthBox component="form" onSubmit={handleSignIn}>
        <FlexRowBox>
          <LeftContent>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h5">{t("authentication.login.login")}</Typography>
                <LanguageSwitcher />
              </Stack>
              <StyledTextField
                placeholder={t("authentication.register.email")}
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <OutlinedInput
                placeholder={t("authentication.register.password")}
                fullWidth
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              {isError && (
                <Alert severity="error">
                  {t("authentication.login.loginFailed")}
                </Alert>
              )}
              <LoginRow>
                <GeneralButton
                  type="submit"
                  loading={isLoading}
                >
                  {t("authentication.login.login")}
                </GeneralButton>
                <Box flexGrow={1} />
                <SimpleLink to={`/${locale}/reset_password`} color="#43b8bb">
                  {t("authentication.login.forgotPassword")}
                </SimpleLink>
              </LoginRow>
              <Divider />
              <Stack spacing={1}>
                <Typography variant="h6" fontWeight="bold">
                  {t("authentication.login.noAccountYet")}
                </Typography>
                <FixedHeightTextBox>
                  <Typography variant="body2" color="textSecondary">
                    {t("authentication.login.registerNow")}
                  </Typography>
                </FixedHeightTextBox>
                <GeneralButton
                  href={`/${locale}/register`}
                >
                  {t("authentication.register.register")}
                </GeneralButton>
              </Stack>
            </Stack>
          </LeftContent>
          <RightContent>
            <BonisoftLogo imgRatio={30} />
          </RightContent>
        </FlexRowBox>
      </AuthBox>
    </AuthLayout>
  );
}
